<div class="dialog-layout"
     [class.dialog-layout--popup]="popupMode()"
     [class.dialog-layout--popup-detail]="popupMode() && (activeDialog() || isComposeExpanded())">
  <!-- ======================================================================= -->
  <!-- Left Panel: Thread List                                                  -->
  <!-- ======================================================================= -->
  <div class="dialog-layout__list">
    <div class="dialog-layout__list-header">
      <!-- 新規相談ボタン -->
      <button
        class="dialog-compose-pill"
        (click)="toggleCompose()"
        [attr.aria-label]="ui.newConsultation"
      >
        <mat-icon class="dialog-compose-pill__icon">add</mat-icon>
        <span class="dialog-compose-pill__text">{{ ui.newConsultation }}</span>
      </button>

      <!-- 検索 + フィルター（横並び） -->
      <div class="dialog-filter-row">
        <div class="dialog-search">
          <mat-icon class="dialog-search__icon">search</mat-icon>
          <input
            class="dialog-search__input"
            type="text"
            [attr.placeholder]="ui.search"
            [value]="filterText()"
            (input)="filterText.set($any($event.target).value)"
            [attr.aria-label]="ui.searchConsultation"
          />
          @if (filterText()) {
            <button
              class="dialog-search__clear"
              (click)="filterText.set('')"
              [attr.aria-label]="ui.clearSearch"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </div>
        <mat-chip-listbox [attr.aria-label]="ui.filter" class="dialog-filter-chips">
          <mat-chip-option
            [selected]="showOnlyOpen()"
            (selectionChange)="onOpenFilterChange($event)"
            color="primary"
          >
            <mat-icon matChipAvatar>pending_actions</mat-icon>
            <span>{{ ui.openOnly }}{{ openCount() > 0 ? ' (' + openCount() + ')' : '' }}</span>
          </mat-chip-option>
        </mat-chip-listbox>
      </div>
    </div>

    <div class="dialog-layout__list-body" role="listbox" [attr.aria-label]="ui.consultationList">
      @if (isLoading()) {
        <div role="status" [attr.aria-label]="ui.loading">
          <span class="visually-hidden">{{ ui.loadingText }}</span>
          @for (i of [1, 2, 3, 4]; track i) {
            <div class="dialog-skeleton">
              <div class="dialog-skeleton__avatar"></div>
              <div class="dialog-skeleton__lines">
                <div class="dialog-skeleton__line dialog-skeleton__line--medium"></div>
                <div class="dialog-skeleton__line dialog-skeleton__line--short"></div>
                <div class="dialog-skeleton__line dialog-skeleton__line--long"></div>
              </div>
            </div>
          }
        </div>
      } @else {
        @for (dialogData of filteredDialogs(); track dialogData.id) {
          <div
            class="dialog-item"
            [class.dialog-item--active]="isDialogSelected(dialogData)"
            (click)="selectDialog(dialogData)"
            (keydown.enter)="selectDialog(dialogData)"
            role="option"
            [attr.aria-selected]="isDialogSelected(dialogData)"
            tabindex="0"
          >
            <span class="dialog-item__avatar">
              {{ getInitials(dialogData.meta.givenBy) }}
            </span>
            <div class="dialog-item__content">
              <div class="dialog-item__header">
                <span class="dialog-item__name">{{ getFullName(dialogData.meta.givenBy) }}</span>
                <time class="dialog-item__time" [attr.datetime]="getLatestTime(dialogData)">
                  {{ getLatestTime(dialogData) | chatDate }}
                </time>
              </div>
              <div class="dialog-item__status-row">
                <span
                  class="dialog-item__badge"
                  [class.dialog-item__badge--open]="getDialogStatus(dialogData) === 'open'"
                  [class.dialog-item__badge--closed]="getDialogStatus(dialogData) === 'closed'"
                >
                  {{ getStatusLabel(getDialogStatus(dialogData)) }}
                </span>
                @if (getReplyCount(dialogData) > 0) {
                  <span class="dialog-item__reply-count">
                    <mat-icon class="dialog-item__reply-icon">chat_bubble_outline</mat-icon>
                    {{ getReplyCount(dialogData) }}
                  </span>
                }
              </div>
              <div class="dialog-item__preview">
                {{ getQuestionSnippet(dialogData) | slice: 0 : 40 }}
              </div>
            </div>
          </div>
        } @empty {
          <div class="dialog-list__empty">
            <mat-icon>forum</mat-icon>
            <span>{{ ui.noConsultation }}</span>
          </div>
        }
      }
    </div>
  </div>

  <!-- ======================================================================= -->
  <!-- Right Panel: Conversation Detail                                         -->
  <!-- ======================================================================= -->
  <div class="dialog-layout__detail" role="region" [attr.aria-label]="ui.consultationDetail">
    <!-- 新規投稿フォーム（展開時） -->
    @if (isComposeExpanded()) {
      <div class="dialog-detail__compose">
        <div class="dialog-detail__compose-header">
          @if (popupMode()) {
            <button
              mat-icon-button
              class="dialog-detail__back-btn"
              (click)="collapseCompose()"
              [attr.aria-label]="ui.backToList"
            >
              <mat-icon>arrow_back</mat-icon>
            </button>
          }
          <mat-icon class="dialog-detail__compose-icon">edit_note</mat-icon>
          <span class="dialog-detail__compose-title">{{ ui.newConsultation }}</span>
          @if (!popupMode()) {
            <button
              mat-icon-button
              (click)="collapseCompose()"
              [attr.aria-label]="ui.close"
              class="dialog-detail__compose-close"
            >
              <mat-icon>close</mat-icon>
            </button>
          }
        </div>
        <div class="dialog-detail__compose-body">
          <app-mention-textarea
            #mentionTextarea
            [mentionTargetUsers]="mentionTargetUserNames"
            [mentionTargetRecords]="mentionTargetRecordNames"
            [innerId]="'mention-textarea-0'"
            [placeholder]="ui.composePlaceholder"
            innerClass="comment_add mention-textarea--new-question"
          />
          <div class="dialog-detail__compose-actions">
            @if (selectedRecordId !== '') {
              <button
                mat-stroked-button
                type="button"
                (click)="mentionToCurrentRecord('0')"
              >
                <mat-icon>link</mat-icon>
                {{ getMentionCurrentLabel() }}
              </button>
            }
            <span class="dialog-detail__spacer"></span>
            <button
              mat-stroked-button
              type="button"
              (click)="clearDialogQuestion()"
            >
              {{ ui.cancel }}
            </button>
            <button
              mat-flat-button
              color="primary"
              type="button"
              (click)="createDialogQuestion()"
              [disabled]="isSending()"
            >
              @if (isSending()) {
                <mat-spinner diameter="18"></mat-spinner>
              }
              {{ ui.post }}
            </button>
          </div>
        </div>
      </div>
    } @else if (activeDialog(); as dialogData) {
      <!-- 選択されたスレッドの会話表示 -->
      <div class="dialog-detail__header">
        @if (popupMode()) {
          <button
            mat-icon-button
            class="dialog-detail__back-btn"
            (click)="deselectDialog()"
            [attr.aria-label]="ui.backToList"
          >
            <mat-icon>arrow_back</mat-icon>
          </button>
        }
        <span class="dialog-detail__avatar">
          {{ getInitials(dialogData.meta.givenBy) }}
        </span>
        <div class="dialog-detail__sender-info">
          <div class="dialog-detail__sender-name">{{ getFullName(dialogData.meta.givenBy) }}</div>
          <div class="dialog-detail__sender-meta">
            <span>({{ databaseCashe.getJobTitle(dialogData.meta.givenBy.jobTitleCode) }})</span>
            <span class="dialog-detail__meta-separator">|</span>
            <span>{{ dialogData.meta.location.sectionName }}</span>
            <span class="dialog-detail__meta-separator">|</span>
            <time [attr.datetime]="dialogData.data.createdAt">
              {{ dialogData.data.createdAt | chatDate }}
            </time>
            <span
              class="dialog-detail__status-badge"
              [class.dialog-detail__status-badge--open]="getDialogStatus(dialogData) === 'open'"
              [class.dialog-detail__status-badge--closed]="getDialogStatus(dialogData) === 'closed'"
            >
              {{ getStatusLabel(getDialogStatus(dialogData)) }}
            </span>
          </div>
        </div>
        @if (canChangeStatus(dialogData)) {
          <button
            class="dialog-detail__status-btn"
            [class.dialog-detail__status-btn--complete]="getDialogStatus(dialogData) === 'open'"
            [class.dialog-detail__status-btn--reopen]="getDialogStatus(dialogData) === 'closed'"
            (click)="toggleStatusLocal(dialogData)"
            [attr.aria-label]="getStatusButtonLabel(getDialogStatus(dialogData))"
          >
            <span class="dialog-detail__status-btn-icon">{{ getStatusButtonIcon(getDialogStatus(dialogData)) }}</span>
            <span>{{ getStatusButtonLabel(getDialogStatus(dialogData)) }}</span>
          </button>
        }
      </div>

      <!-- 会話エリア -->
      <div
        class="chat-history"
        [class.chat-history--closed]="getDialogStatus(dialogData) === 'closed'"
      >
        <ul class="chat-history__list">
          <!-- 質問メッセージ -->
          <li
            class="chat-history__item"
            [class.chat-history__item--own]="isOwnMessage(dialogData.meta.givenBy.userId)"
          >
            <div
              class="message-data"
              [class.message-data--end]="isOwnMessage(dialogData.meta.givenBy.userId)"
            >
              @if (isOwnMessage(dialogData.meta.givenBy.userId)) {
                <span class="message-data__time">{{ dialogData.data.createdAt | chatDate }}</span>
                <span class="message-data__name">{{ getFullName(dialogData.meta.givenBy) }}</span>
              } @else {
                <span class="message-data__name">{{ getFullName(dialogData.meta.givenBy) }}</span>
                <span class="message-data__time">{{ dialogData.data.createdAt | chatDate }}</span>
              }
            </div>
            <div
              class="message"
              [class.message--my]="!isOwnMessage(dialogData.meta.givenBy.userId)"
              [class.message--other]="isOwnMessage(dialogData.meta.givenBy.userId)"
              [id]="'dialog-content_' + dialogData.id + '-0'"
              [innerHtml]="genMentionHtml(dialogData.data.questionText, loginUserId, dialogData.id, 0)"
            ></div>
          </li>

          <!-- 返信メッセージ -->
          @if (dialogData.data.responses && dialogData.data.responses.length > 0) {
            @for (respons of dialogData.data.responses; track $index; let responseIndex = $index) {
              <li
                class="chat-history__item"
                [class.chat-history__item--own]="isOwnMessage(respons.givenBy.userId)"
              >
                <div
                  class="message-data"
                  [class.message-data--end]="isOwnMessage(respons.givenBy.userId)"
                >
                  @if (isOwnMessage(respons.givenBy.userId)) {
                    <span class="message-data__time">
                      {{ respons.createdAt | chatDate }}
                      @if (respons.updated) {
                        {{ ui.edited }}
                      }
                    </span>
                    <span class="message-data__name">{{ getFullName(respons.givenBy) }}</span>
                  } @else {
                    <span class="message-data__name">{{ getFullName(respons.givenBy) }}</span>
                    <span class="message-data__time">
                      {{ respons.createdAt | chatDate }}
                      @if (respons.updated) {
                        {{ ui.edited }}
                      }
                    </span>
                  }
                </div>
                <div
                  class="message"
                  [class.message--my]="!isOwnMessage(respons.givenBy.userId)"
                  [class.message--other]="isOwnMessage(respons.givenBy.userId)"
                  [id]="'dialog-content_' + dialogData.id + '-' + convertToResponseIndex(responseIndex)"
                  [innerHtml]="genMentionHtml(respons.responseText, loginUserId, dialogData.id, convertToResponseIndex(responseIndex))"
                ></div>
              </li>
            }
          }
        </ul>
      </div>

      <!-- 返信フォーム（対応済みでなければ表示） -->
      @if (getDialogStatus(dialogData) !== 'closed') {
        <div class="dialog-detail__reply-form">
          <app-mention-textarea
            #mentionTextarea
            [mentionTargetUsers]="mentionTargetUserNames"
            [mentionTargetRecords]="mentionTargetRecordNames"
            [innerId]="'mention-textarea-' + dialogData.id"
            [placeholder]="ui.replyPlaceholder"
            innerClass="comment_add mention-textarea--reply"
          />
          <div class="dialog-detail__reply-actions">
            @if (selectedRecordId !== '') {
              <button
                mat-stroked-button
                type="button"
                (click)="mentionToCurrentRecord(dialogData.id)"
              >
                <mat-icon>link</mat-icon>
                {{ getMentionCurrentLabel() }}
              </button>
            }
            <span class="dialog-detail__spacer"></span>
            <button
              mat-flat-button
              color="primary"
              type="button"
              (click)="addDialogResponse(dialogData)"
              [disabled]="isSending()"
            >
              @if (isSending()) {
                <mat-spinner diameter="18"></mat-spinner>
              }
              {{ ui.reply }}
            </button>
          </div>
        </div>
      }
    } @else {
      <!-- 空の状態 -->
      <div class="dialog-detail__empty">
        <mat-icon>forum</mat-icon>
        <div class="dialog-detail__empty-title">{{ ui.selectConsultation }}</div>
        <div class="dialog-detail__empty-description">
          {{ ui.selectConsultationHelp }}
        </div>
      </div>
    }
  </div>
</div>
